import { useState, useEffect } from 'react';
import { 
  Route, Calendar, Clock, Users, MapPin, ArrowRight, 
  Loader2, Star, User, ChevronRight
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { driverRouteAPI, routeBookingAPI } from '../lib/api';
import { toast } from 'sonner';
import RouteMap from './RouteMap';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
];

const BrowseRoutes = ({ onClose }) => {
  const [routes, setRoutes] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [expandedMap, setExpandedMap] = useState(null);
  const [booking, setBooking] = useState({
    travel_date: '',
    seats_requested: 1,
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [routesRes, bookingsRes] = await Promise.all([
        driverRouteAPI.getAll(),
        routeBookingAPI.getAll().catch(() => ({ data: [] })),
      ]);
      setRoutes(routesRes.data);
      setMyBookings(bookingsRes.data);
    } catch (error) {
      toast.error('Failed to load routes');
    } finally {
      setLoading(false);
    }
  };

  const handleBookRoute = async (e) => {
    e.preventDefault();
    
    if (!booking.travel_date) {
      toast.error('Please select a travel date');
      return;
    }
    
    setSubmitting(true);
    try {
      await routeBookingAPI.create({
        route_id: selectedRoute.id,
        travel_date: booking.travel_date,
        seats_requested: parseInt(booking.seats_requested),
        notes: booking.notes || null,
      });
      toast.success('Route booked successfully!');
      setShowBookingDialog(false);
      setSelectedRoute(null);
      setBooking({ travel_date: '', seats_requested: 1, notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book route');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!confirm('Are you sure you want to cancel this booking?')) return;
    
    try {
      await routeBookingAPI.cancel(bookingId);
      toast.success('Booking cancelled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    }
  };

  const openBookingDialog = (route) => {
    setSelectedRoute(route);
    setShowBookingDialog(true);
  };

  const formatDays = (days) => {
    return days
      .sort((a, b) => a - b)
      .map(d => DAYS_OF_WEEK.find(day => day.value === d)?.label)
      .join(', ');
  };

  const getMinDate = () => {
    const today = new Date();
    today.setDate(today.getDate() + 1);
    return today.toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* My Bookings Section */}
      {myBookings.length > 0 && (
        <div>
          <h3 className="text-base font-semibold text-zinc-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            My Route Bookings
          </h3>
          <div className="space-y-3">
            {myBookings.filter(b => b.status !== 'cancelled').map((booking) => {
              const route = routes.find(r => r.id === booking.route_id);
              return (
                <div
                  key={booking.id}
                  className="bg-orange-50 border border-orange-200 rounded-xl p-4"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-zinc-900">
                        {route?.route_name || 'Route'}
                      </p>
                      <p className="text-sm text-zinc-600">
                        {new Date(booking.travel_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        {' • '}{booking.seats_requested} seat{booking.seats_requested !== 1 ? 's' : ''}
                      </p>
                      {route && (
                        <p className="text-xs text-zinc-500 mt-1">
                          Driver: {route.driver_name}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-zinc-900">${booking.total_price.toFixed(2)}</p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCancelBooking(booking.id)}
                        className="text-red-600 hover:text-red-700 text-xs mt-1"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Available Routes */}
      <div>
        <h3 className="text-base font-semibold text-zinc-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Available Driver Routes
        </h3>
        
        {routes.length === 0 ? (
          <div className="text-center py-8 bg-zinc-50 rounded-xl border border-zinc-200">
            <Route className="w-10 h-10 text-zinc-400 mx-auto mb-3" />
            <p className="text-zinc-600">No scheduled routes available</p>
            <p className="text-sm text-zinc-500 mt-1">Check back later for driver routes</p>
          </div>
        ) : (
          <div className="space-y-4">
            {routes.map((route) => {
              const availableSeats = route.available_seats - route.booked_seats;
              const isFullyBooked = availableSeats <= 0;
              
              return (
                <div
                  key={route.id}
                  className={`bg-white border rounded-xl p-5 ${isFullyBooked ? 'border-zinc-200 opacity-60' : 'border-zinc-200 hover:border-orange-300'}`}
                  data-testid={`browse-route-${route.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-zinc-900">{route.route_name}</h4>
                        {route.driver_rating && (
                          <div className="flex items-center gap-1 text-sm">
                            <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
                            <span className="text-zinc-600">{route.driver_rating}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm text-zinc-600 mb-2">
                        <MapPin className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="truncate max-w-[150px]">{route.origin_address}</span>
                        <ArrowRight className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                        <span className="truncate max-w-[150px]">{route.destination_address}</span>
                      </div>
                      
                      {/* Multi-stop badge */}
                      {route.stops && route.stops.length > 2 && (
                        <div className="flex items-center gap-1.5 mb-2">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">
                            {route.stops.length} stops
                          </span>
                          <button
                            onClick={() => setExpandedMap(expandedMap === route.id ? null : route.id)}
                            className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                          >
                            {expandedMap === route.id ? 'Hide map' : 'View route map'}
                          </button>
                        </div>
                      )}
                      
                      <div className="flex flex-wrap gap-3 text-sm text-zinc-500">
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-4 h-4" />
                          <span>{route.departure_time}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDays(route.days_of_week)}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Users className="w-4 h-4" />
                          <span className={availableSeats <= 2 && !isFullyBooked ? 'text-orange-600 font-medium' : ''}>
                            {availableSeats} seat{availableSeats !== 1 ? 's' : ''} left
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 mt-2 text-sm text-zinc-500">
                        <User className="w-4 h-4" />
                        <span>{route.driver_name}</span>
                        {route.zone_name && (
                          <>
                            <span className="text-zinc-300">•</span>
                            <span className="text-blue-600">{route.zone_name}</span>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-right ml-4">
                      <p className="text-xl font-bold text-zinc-900">${route.price_per_seat.toFixed(2)}</p>
                      <p className="text-xs text-zinc-500 mb-3">per seat</p>
                      
                      <Button
                        onClick={() => openBookingDialog(route)}
                        disabled={isFullyBooked}
                        className={isFullyBooked 
                          ? 'bg-zinc-200 text-zinc-500 cursor-not-allowed' 
                          : 'bg-orange-500 hover:bg-orange-600 text-white'}
                        size="sm"
                        data-testid={`book-route-${route.id}`}
                      >
                        {isFullyBooked ? 'Full' : 'Book'}
                        {!isFullyBooked && <ChevronRight className="w-4 h-4 ml-1" />}
                      </Button>
                    </div>
                  </div>
                  
                  {route.notes && (
                    <p className="mt-3 pt-3 border-t border-zinc-100 text-sm text-zinc-500">
                      {route.notes}
                    </p>
                  )}
                  
                  {/* Route Map */}
                  {expandedMap === route.id && route.stops && (
                    <div className="mt-3 pt-3 border-t border-zinc-100">
                      <RouteMap stops={route.stops} height="220px" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Booking Dialog */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Book Route</DialogTitle>
            <DialogDescription>
              Reserve your seat on this scheduled route
            </DialogDescription>
          </DialogHeader>
          
          {selectedRoute && (
            <form onSubmit={handleBookRoute} className="space-y-4 pt-2">
              {/* Route Summary */}
              <div className="bg-zinc-50 rounded-lg p-4">
                <p className="font-medium text-zinc-900">{selectedRoute.route_name}</p>
                <div className="flex items-center gap-2 text-sm text-zinc-600 mt-1">
                  <MapPin className="w-4 h-4 text-zinc-400" />
                  <span>{selectedRoute.origin_address}</span>
                  <ArrowRight className="w-4 h-4 text-zinc-400" />
                  <span>{selectedRoute.destination_address}</span>
                </div>
                <div className="flex items-center gap-3 mt-2 text-sm text-zinc-500">
                  <span>{selectedRoute.departure_time}</span>
                  <span>•</span>
                  <span>Driver: {selectedRoute.driver_name}</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="travel_date">Travel Date</Label>
                <Input
                  id="travel_date"
                  type="date"
                  min={getMinDate()}
                  value={booking.travel_date}
                  onChange={(e) => setBooking(prev => ({ ...prev, travel_date: e.target.value }))}
                  required
                  data-testid="booking-date-input"
                />
                <p className="text-xs text-zinc-500">
                  This route runs on: {formatDays(selectedRoute.days_of_week)}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="seats_requested">Number of Seats</Label>
                <Input
                  id="seats_requested"
                  type="number"
                  min="1"
                  max={selectedRoute.available_seats - selectedRoute.booked_seats}
                  value={booking.seats_requested}
                  onChange={(e) => setBooking(prev => ({ ...prev, seats_requested: e.target.value }))}
                  required
                  data-testid="booking-seats-input"
                />
                <p className="text-xs text-zinc-500">
                  {selectedRoute.available_seats - selectedRoute.booked_seats} seats available
                </p>
              </div>
              
              <div className="bg-orange-50 rounded-lg p-4 flex items-center justify-between">
                <span className="text-zinc-700">Total Price</span>
                <span className="text-xl font-bold text-zinc-900">
                  ${(selectedRoute.price_per_seat * (parseInt(booking.seats_requested) || 1)).toFixed(2)}
                </span>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowBookingDialog(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                  disabled={submitting}
                  data-testid="confirm-booking-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Booking...
                    </>
                  ) : (
                    'Confirm Booking'
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrowseRoutes;
